import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Grid,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  Fab,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  AssignmentInd as AssignIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  PendingActions as PendingActionsIcon,
  Autorenew as AutorenewIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import {
  useRequests,
  useRequestStats,
  useCreateRequest,
  useAssignRequest,
  useMarkInProgress,
  useCompleteRequest,
  useUsers,
} from '../hooks/useRequests';
import { Request } from '../types';
import { format } from 'date-fns';

const RequestsPage: React.FC = () => {
  const [viewDialog, setViewDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    other_details: '',
    assigned_to: '',
  });

  const [assignUserId, setAssignUserId] = useState('');
  const [completionNote, setCompletionNote] = useState('');

  // React Query hooks
  const { data: requestsData, error, isLoading, refetch, isFetching } = useRequests();
  const { data: stats, isFetching: isStatsFetching } = useRequestStats();
  const { data: users, isFetching: isUsersFetching } = useUsers();
  const createMutation = useCreateRequest();
  const assignMutation = useAssignRequest();
  const inProgressMutation = useMarkInProgress();
  const completeMutation = useCompleteRequest();

  // Filter requests based on status
  const filteredRequests = React.useMemo(() => {
    if (!requestsData?.data) return [];
    if (statusFilter === 'all') return requestsData.data;
    return requestsData.data.filter((req: Request) => req.status === statusFilter);
  }, [requestsData, statusFilter]);

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: 3,
            }}
          >
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'transparent !important',
                border: 'none',
                color: '#ffffff',
                padding: 0,
                '& .MuiAlert-icon': { color: '#f44336' },
                '& .MuiAlert-message': { color: '#ffffff', padding: 0 },
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff !important' }}>
                Failed to Load Requests Data
              </Typography>
              <Typography
                variant="body2"
                paragraph
                sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}
              >
                {error instanceof Error
                  ? error.message
                  : 'An error occurred while fetching requests. Please try again.'}
              </Typography>
            </Alert>
          </Paper>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)' },
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.category}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {params.value?.name || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.value?.username || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'assigned_to',
      headerName: 'Assigned To',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {params.value?.name || 'Unassigned'}
          </Typography>
          {params.value?.username && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.value.username}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as string;
        const statusConfig = {
          pending: { bg: 'rgba(255, 152, 0, 0.1)', text: '#ff9800', label: 'Pending' },
          in_progress: { bg: 'rgba(33, 150, 243, 0.1)', text: '#2196f3', label: 'In Progress' },
          completed: { bg: 'rgba(46, 125, 50, 0.1)', text: '#2e7d32', label: 'Completed' },
          cancelled: { bg: 'rgba(158, 158, 158, 0.1)', text: '#616161', label: 'Cancelled' },
        };
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Chip
              label={config.label}
              size="small"
              sx={{
                bgcolor: config.bg,
                color: config.text,
                fontWeight: 600,
                minWidth: 100,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) => {
        try {
          return format(new Date(value), 'MMM dd, yyyy');
        } catch {
          return value;
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" gap={0.5} justifyContent="center" alignItems="center">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedRequest(params.row);
              setViewDialog(true);
            }}
            sx={{
              color: '#834105',
              bgcolor: 'rgba(131, 65, 5, 0.08)',
              '&:hover': { bgcolor: 'rgba(131, 65, 5, 0.15)' },
              width: 32,
              height: 32,
            }}
            title="View Details"
          >
            <ViewIcon sx={{ fontSize: 18 }} />
          </IconButton>
          {params.row.status === 'pending' && (
            <>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedRequest(params.row);
                  setAssignDialog(true);
                }}
                sx={{
                  color: '#2196f3',
                  bgcolor: 'rgba(33, 150, 243, 0.08)',
                  '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.15)' },
                  width: 32,
                  height: 32,
                }}
                title="Assign"
              >
                <AssignIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleStartProgress(params.row.id)}
                sx={{
                  color: '#ff9800',
                  bgcolor: 'rgba(255, 152, 0, 0.08)',
                  '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.15)' },
                  width: 32,
                  height: 32,
                }}
                title="Start"
              >
                <StartIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          )}
          {params.row.status === 'in_progress' && (
            <IconButton
              size="small"
              onClick={() => {
                setSelectedRequest(params.row);
                setCompleteDialog(true);
              }}
              sx={{
                color: '#4caf50',
                bgcolor: 'rgba(76, 175, 80, 0.08)',
                '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.15)' },
                width: 32,
                height: 32,
              }}
              title="Complete"
            >
              <CompleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  // Handlers
  const handleCreateRequest = async () => {
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        other_details: formData.other_details || undefined,
        assigned_to: formData.assigned_to ? Number(formData.assigned_to) : undefined,
      };
      await createMutation.mutateAsync(payload);
      setAddDialog(false);
      resetForm();
    } catch (error: any) {
      console.error('Failed to create request:', error);
    }
  };

  const handleAssignRequest = async () => {
    if (!selectedRequest || !assignUserId) return;
    try {
      await assignMutation.mutateAsync({
        id: selectedRequest.id,
        userId: Number(assignUserId),
      });
      setAssignDialog(false);
      setAssignUserId('');
    } catch (error: any) {
      console.error('Failed to assign request:', error);
    }
  };

  const handleStartProgress = async (id: number) => {
    try {
      await inProgressMutation.mutateAsync(id);
    } catch (error: any) {
      console.error('Failed to start request:', error);
    }
  };

  const handleCompleteRequest = async () => {
    if (!selectedRequest) return;
    try {
      await completeMutation.mutateAsync({
        id: selectedRequest.id,
        data: { completion_note: completionNote },
      });
      setCompleteDialog(false);
      setCompletionNote('');
    } catch (error: any) {
      console.error('Failed to complete request:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      other_details: '',
      assigned_to: '',
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isFetching || isStatsFetching || isUsersFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Page Header */}
      <Box
        sx={{
          mb: 3.5,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #161616 0%, #1f1f1f 100%)',
          border: '1px solid rgba(255,152,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(156,39,176,0.15)', color: '#ab47bc', width: 52, height: 52 }}>
          <AssignmentIcon fontSize="medium" />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
          >
            Requests
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
            Manage, assign and track farm requests and tasks
          </Typography>
        </Box>
      </Box>

      <Fab
        color="primary"
        onClick={() => setAddDialog(true)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          color: '#ffffff',
          boxShadow: '0px 6px 20px rgba(255, 152, 0, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
            boxShadow: '0px 8px 24px rgba(255, 152, 0, 0.5)',
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2.5} mb={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper
              sx={{
                p: 2.5,
                background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
                border: '1px solid rgba(156,39,176,0.15)',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  border: '1px solid rgba(156,39,176,0.35)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                },
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(156,39,176,0.12)',
                    color: '#ab47bc',
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                  }}
                >
                  <AssignmentTurnedInIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      letterSpacing: '0.8px',
                      color: 'rgba(255,152,0,0.7)',
                      fontSize: '0.65rem',
                      display: 'block',
                    }}
                  >
                    TOTAL REQUESTS
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: '#ab47bc', lineHeight: 1.2, mt: 0.25 }}
                  >
                    {stats.total || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                  >
                    all time
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper
              sx={{
                p: 2.5,
                background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
                border: '1px solid rgba(255,152,0,0.15)',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  border: '1px solid rgba(255,152,0,0.35)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                },
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,152,0,0.12)',
                    color: '#ff9800',
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                  }}
                >
                  <PendingActionsIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      letterSpacing: '0.8px',
                      color: 'rgba(255,152,0,0.7)',
                      fontSize: '0.65rem',
                      display: 'block',
                    }}
                  >
                    PENDING
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: '#ff9800', lineHeight: 1.2, mt: 0.25 }}
                  >
                    {stats.pending || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                  >
                    awaiting action
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper
              sx={{
                p: 2.5,
                background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
                border: '1px solid rgba(33,150,243,0.15)',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  border: '1px solid rgba(33,150,243,0.35)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                },
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(33,150,243,0.12)',
                    color: '#42a5f5',
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                  }}
                >
                  <AutorenewIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      letterSpacing: '0.8px',
                      color: 'rgba(255,152,0,0.7)',
                      fontSize: '0.65rem',
                      display: 'block',
                    }}
                  >
                    IN PROGRESS
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: '#42a5f5', lineHeight: 1.2, mt: 0.25 }}
                  >
                    {stats.in_progress || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                  >
                    being worked on
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper
              sx={{
                p: 2.5,
                background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
                border: '1px solid rgba(102,187,106,0.15)',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  border: '1px solid rgba(102,187,106,0.35)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                },
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(102,187,106,0.12)',
                    color: '#66bb6a',
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                  }}
                >
                  <AssignmentTurnedInIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      letterSpacing: '0.8px',
                      color: 'rgba(255,152,0,0.7)',
                      fontSize: '0.65rem',
                      display: 'block',
                    }}
                  >
                    COMPLETED
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: '#66bb6a', lineHeight: 1.2, mt: 0.25 }}
                  >
                    {stats.completed || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                  >
                    resolved
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filter */}
      <Box
        mb={2}
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(131, 65, 5, 0.3)',
        }}
      >
        <FormControl
          size="small"
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              color: '#ffffff',
              '& fieldset': {
                borderColor: 'rgba(131, 65, 5, 0.3)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(131, 65, 5, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#834105',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-focused': {
                color: '#834105',
              },
            },
            '& .MuiSelect-icon': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          }}
        >
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            label="Filter by Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Requests</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Data Grid */}
      <Paper
        sx={{ p: 0, border: '1px solid rgba(255,152,0,0.2)', borderRadius: 2, overflow: 'hidden' }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
            borderBottom: '1px solid rgba(255,152,0,0.15)',
          }}
        >
          <AssignmentIcon sx={{ color: '#ff9800', fontSize: '1.1rem' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff' }}>
            Request Records
          </Typography>
          <Chip
            label={filteredRequests.length}
            size="small"
            sx={{ bgcolor: 'rgba(255,152,0,0.12)', color: '#ff9800', fontWeight: 700, height: 20 }}
          />
        </Box>
        <DataGrid
          rows={filteredRequests || []}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          getRowHeight={() => 'auto'}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 50, page: 0 },
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255,152,0,0.1)',
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              color: '#ffffff',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: '0.8px',
              color: 'rgba(255,152,0,0.85)',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row': {
              minHeight: '70px !important',
              '&:hover': { bgcolor: 'rgba(255,152,0,0.06)' },
            },
            '& .MuiDataGrid-footerContainer': { borderTop: '1px solid rgba(255,152,0,0.15)' },
          }}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } } }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(131, 65, 5, 0.3)',
            pb: 2.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(131, 65, 5, 0.2)',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
              }}
            >
              <AssignmentTurnedInIcon sx={{ color: '#834105', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#834105' }}>
                Request Details
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                View complete request information
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRequest && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Title
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {selectedRequest.title}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Category
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {(selectedRequest as any).category}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Created By
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {(selectedRequest as any).created_by?.name || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Assigned To
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {(selectedRequest as any).assigned_to?.name || 'Unassigned'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Status
                </Typography>
                <Box mt={0.5}>
                  <Chip label={selectedRequest.status} size="small" sx={{ fontWeight: 600 }} />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Created
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {format(new Date(selectedRequest.created_at), 'MMMM dd, yyyy HH:mm')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Description
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {selectedRequest.description}
                </Typography>
              </Grid>
              {(selectedRequest as any).other_details && (
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Other Details
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {(selectedRequest as any).other_details}
                  </Typography>
                </Grid>
              )}
              {(selectedRequest as any).completion_note && (
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Completion Note
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {(selectedRequest as any).completion_note}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid rgba(131, 65, 5, 0.3)',
            backgroundColor: 'rgba(131, 65, 5, 0.05)',
          }}
        >
          <Button onClick={() => setViewDialog(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } } }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(131, 65, 5, 0.3)',
            color: '#834105',
          }}
        >
          Create New Request
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(131, 65, 5, 0.2)' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                required
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <MenuItem value="feed">Feed</MenuItem>
                <MenuItem value="medication">Medication</MenuItem>
                <MenuItem value="rubbish_pickup">Rubbish Pickup</MenuItem>
                <MenuItem value="others">Others</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={
                  formData.category === 'others'
                    ? 'Other Details (Required)'
                    : 'Other Details (Optional)'
                }
                value={formData.other_details}
                onChange={(e) => setFormData({ ...formData, other_details: e.target.value })}
                placeholder="Additional information..."
                required={formData.category === 'others'}
                error={formData.category === 'others' && !formData.other_details}
                helperText={
                  formData.category === 'others' && !formData.other_details
                    ? 'Required when category is Others'
                    : ''
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="Assign To (Optional)"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {users?.map((user: any) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid rgba(131, 65, 5, 0.3)',
            backgroundColor: 'rgba(131, 65, 5, 0.05)',
          }}
        >
          <Button onClick={() => setAddDialog(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRequest}
            disabled={
              !formData.title ||
              !formData.description ||
              !formData.category ||
              (formData.category === 'others' && !formData.other_details) ||
              createMutation.isPending
            }
            sx={{
              background: 'linear-gradient(135deg, #834105 0%, #9e4f06 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #5d2e02 0%, #834105 100%)' },
              '&:disabled': { background: 'rgba(131, 65, 5, 0.3)' },
            }}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog
        open={assignDialog}
        onClose={() => setAssignDialog(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: { sx: { border: '1px solid rgba(33,150,243,0.35)', borderRadius: 2 } },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(33, 150, 243, 0.3)',
            color: '#42a5f5',
          }}
        >
          Assign Request
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(33, 150, 243, 0.2)' }}>
          <TextField
            select
            fullWidth
            label="Assign To"
            value={assignUserId}
            onChange={(e) => setAssignUserId(e.target.value)}
          >
            {users?.map((user: any) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid rgba(33, 150, 243, 0.3)',
            backgroundColor: 'rgba(33, 150, 243, 0.05)',
          }}
        >
          <Button onClick={() => setAssignDialog(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignRequest}
            disabled={!assignUserId || assignMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)' },
              '&:disabled': { background: 'rgba(33, 150, 243, 0.3)' },
            }}
          >
            {assignMutation.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog
        open={completeDialog}
        onClose={() => setCompleteDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { border: '1px solid rgba(102,187,106,0.35)', borderRadius: 2 } },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
            color: '#66bb6a',
          }}
        >
          Complete Request
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(76, 175, 80, 0.2)' }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Completion Note (Optional)"
            value={completionNote}
            onChange={(e) => setCompletionNote(e.target.value)}
            placeholder="Add any completion notes or comments..."
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid rgba(76, 175, 80, 0.3)',
            backgroundColor: 'rgba(76, 175, 80, 0.05)',
          }}
        >
          <Button
            onClick={() => setCompleteDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCompleteRequest}
            disabled={completeMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)' },
              '&:disabled': { background: 'rgba(76, 175, 80, 0.3)' },
            }}
          >
            {completeMutation.isPending ? 'Completing...' : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestsPage;
